import { AppError } from '../core';
import { ErrorReportOperation } from './error-report-operation';

/** Whether a crash broke the app (fatal) or was recoverable (non-fatal). */
export type ErrorReportSeverity = 'fatal' | 'non-fatal';

/**
 * Runtime/environment snapshot attached to a report. Collected by the caller
 * (so this module stays pure and SSR-agnostic) and merged into the
 * {@link ErrorReportContext}.
 */
export interface ErrorReportEnvironment {
  /** Failed application name, e.g. `imagise-app` / `imagise-cloud`. */
  appName: string;

  /** Application version, when known. */
  appVersion: string | null;

  /** `true` for production builds. */
  isProduction: boolean;

  /** Whether the crash broke the app or was recoverable. */
  severity: ErrorReportSeverity;

  /** Full page URL where the error happened (`location.href`). */
  pageUrl: string;

  /** In-app route path (`Router.url`), when available. */
  routePath?: string;

  /** Browser user-agent string. */
  userAgent: string;

  /** Whether the browser reported an online connection at capture time. */
  online: boolean;

  /** ISO-8601 capture time. */
  timestamp: string;
}

/**
 * Full diagnostic context for a captured error: the environment snapshot plus
 * the normalized error chain. This is what gets redacted and then turned into
 * an {@link ErrorReportRequest}.
 */
export interface ErrorReportContext extends ErrorReportEnvironment {
  kind?: 'error' | 'feedback';
  operation?: ErrorReportOperation;
  /** Top-level, human-readable error message. */
  message: string;

  /** Error class/type name. */
  type: string;

  /** Normalized error chain (nested causes become `InnerException`). */
  errors: AppError[];
}

/**
 * Normalizes any thrown value into the backend {@link AppError} shape,
 * recursively unwrapping an `Error.cause` chain into `InnerException`.
 *
 * Handles native `Error`s, plain objects carrying `message`/`name`/`stack`,
 * strings, and arbitrary values without throwing. A cyclic `cause` chain (e.g.
 * `a.cause === b` and `b.cause === a`, or an error that is its own cause) is
 * cut off at the first already-seen error, so normalization can never recurse
 * forever.
 */
export function toAppError(error: unknown): AppError {
  return normalizeError(error, new WeakSet<object>());
}

/**
 * Recursive core of {@link toAppError}. `seen` tracks the `Error` objects already
 * visited on the current `cause` chain so a self-referential or looping chain is
 * broken instead of overflowing the stack.
 */
const MAX_CAUSE_DEPTH = 8;

function normalizeError(error: unknown, seen: WeakSet<object>, depth = 0): AppError {
  if (typeof error === 'object' && error !== null) {
    seen.add(error);
    const record = error as Record<string, unknown>;
    // Collect only diagnostic fields; arbitrary thrown objects can contain session data.
    const result: AppError = {
      ExceptionMessage: typeof record['message'] === 'string' ? record['message'] : 'Unknown error',
      Type: typeof record['name'] === 'string' ? record['name'] : 'Error',
      Details: typeof record['stack'] === 'string' ? record['stack'] : '',
    };
    const cause = record['cause'];
    if (cause != null && depth + 1 < MAX_CAUSE_DEPTH &&
        !(typeof cause === 'object' && seen.has(cause))) {
      result.InnerException = normalizeError(cause, seen, depth + 1);
    }
    return result;
  }
  return { ExceptionMessage: String(error), Type: 'Error', Details: '' };
}

/**
 * Builds the diagnostic context from a thrown value and a pre-collected
 * environment snapshot. Pure: all runtime/SSR-sensitive lookups happen in the
 * caller and arrive via {@link ErrorReportEnvironment}.
 */
export function buildErrorReportContext(
  error: unknown,
  environment: ErrorReportEnvironment
): ErrorReportContext {
  const errors = [toAppError(error)];

  return {
    appName: environment.appName,
    appVersion: environment.appVersion,
    isProduction: environment.isProduction,
    severity: environment.severity,
    pageUrl: environment.pageUrl,
    routePath: environment.routePath,
    userAgent: environment.userAgent,
    online: environment.online,
    timestamp: environment.timestamp,
    message: errors[0].ExceptionMessage,
    type: errors[0].Type,
    errors,
  };
}
